import { Skeleton } from '../ui/skeleton';
import { Surface } from '../layout/Surface';

// The project-page skeletons are early-returned *instead of* their page, so
// they have to supply the same <Surface> the loaded page does — otherwise they
// render bare on the ambient canvas for the length of the fetch.

export function DashboardSkeleton() {
    return (
        <div className="min-h-screen bg-black text-white relative overflow-hidden">
            {/* Background Gradients */}
            <div className="absolute top-[-20%] right-[-10%] w-[600px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] pointer-events-none" />
            <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-purple-600/10 rounded-full blur-[100px] pointer-events-none" />

            <div className="container mx-auto px-6 py-12 relative z-10 max-w-7xl">
                {/* Header Skeleton */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-6">
                    <div className="flex items-center gap-4">
                        <Skeleton className="h-14 w-14 rounded-xl" />
                        <div>
                            <Skeleton className="h-8 w-32 mb-2" />
                            <Skeleton className="h-4 w-48" />
                        </div>
                    </div>
                    <div className="flex items-center gap-4 w-full md:w-auto">
                        <Skeleton className="h-10 w-80 rounded-lg" />
                    </div>
                </div>

                {/* Hero Skeleton */}
                <Skeleton className="h-48 w-full rounded-2xl mb-8" />

                {/* Grid Skeleton */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {[...Array(4)].map((_, i) => (
                        <Skeleton key={i} className="h-56 rounded-xl" />
                    ))}
                </div>
            </div>
        </div>
    );
}

/**
 * Mirrors the console's geometry with plain divs — deliberately no PanelGroup.
 * A group here would register panels and write a layout to localStorage for a
 * pane set that exists only while the project is loading.
 *
 * The proportions are the console's defaults (62/38, 58/42, 38/30/32), so the
 * skeleton doesn't visibly re-flow the moment real content lands.
 */
function PaneSkeleton({ rows = 4 }) {
    return (
        <Surface variant="tile" className="h-full">
            <div className="flex h-9 flex-shrink-0 items-center gap-2 border-b border-white/[0.06] px-3">
                <Skeleton className="h-3.5 w-3.5 rounded" />
                <Skeleton className="h-2.5 w-16" />
            </div>
            <div className="flex-1 space-y-2 p-3">
                {[...Array(rows)].map((_, i) => (
                    <Skeleton key={i} className="h-8 rounded-lg" />
                ))}
            </div>
        </Surface>
    );
}

export function ProjectHomeSkeleton() {
    return (
        <div className="flex min-h-0 min-w-0 flex-1 flex-col md:gap-3">
            {/* Instrument bar */}
            <Surface as="header" variant="bar" className="flex-shrink-0">
                <div className="flex items-center gap-3 px-3 py-2">
                    <Skeleton className="h-8 w-8 rounded-xl" />
                    <Skeleton className="h-4 w-36" />
                    <div className="ml-auto flex items-center gap-1.5">
                        <Skeleton className="h-8 w-20 rounded-xl" />
                        <Skeleton className="h-8 w-20 rounded-xl" />
                        <Skeleton className="h-8 w-24 rounded-xl" />
                    </div>
                </div>
            </Surface>

            {/* Pane tree. gap-3 stands in for the 12px resize handles. */}
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-y-auto px-3 pb-28 pt-3 md:flex-row md:overflow-hidden md:p-0 md:pb-0">
                <div className="flex min-h-0 flex-col gap-3 md:w-[62%]">
                    <div className="h-[380px] md:h-auto md:flex-[58]"><PaneSkeleton rows={5} /></div>
                    <div className="h-[210px] md:h-auto md:flex-[42]"><PaneSkeleton rows={2} /></div>
                </div>
                <div className="flex min-h-0 flex-col gap-3 md:w-[38%]">
                    <div className="h-[280px] md:h-auto md:flex-[38]"><PaneSkeleton rows={3} /></div>
                    <div className="h-[260px] md:h-auto md:flex-[30]"><PaneSkeleton rows={2} /></div>
                    <div className="h-[300px] md:h-auto md:flex-[32]"><PaneSkeleton rows={3} /></div>
                </div>
            </div>
        </div>
    );
}

export function TasksSkeleton() {
    return (
        <Surface className="flex-1">
            {/* Header */}
            <div className="flex-shrink-0 p-6 lg:px-8 lg:pt-8 pb-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                    <div>
                        <Skeleton className="h-8 w-48 mb-2" />
                        <Skeleton className="h-4 w-64" />
                    </div>
                    <Skeleton className="h-10 w-64 rounded-2xl" />
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden p-6 lg:p-8 pt-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 h-full">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="flex flex-col rounded-2xl bg-secondary/20 border border-white/10 h-full">
                            <div className="p-4 border-b border-white/5">
                                <Skeleton className="h-6 w-32" />
                            </div>
                            <div className="flex-1 p-3 space-y-2">
                                {[...Array(4)].map((_, j) => (
                                    <Skeleton key={j} className="h-20 rounded-xl" />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Surface>
    );
}

export function ChatSkeleton() {
    return (
        <div className="h-full flex bg-background/50 relative">
            {/* Sidebar */}
            <div className="w-72 flex-shrink-0 border-r border-white/5 bg-black/30 backdrop-blur-sm">
                <div className="p-4 h-full flex flex-col">
                    <div className="flex items-center justify-between mb-4">
                        <Skeleton className="h-5 w-28" />
                        <Skeleton className="h-8 w-16 rounded-xl" />
                    </div>
                    <div className="space-y-2">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} className="h-16 rounded-xl" />
                        ))}
                    </div>
                </div>
            </div>

            {/* Main Chat Area */}
            <div className="flex-1 flex flex-col min-w-0 relative">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto pb-40">
                    <div className="max-w-4xl mx-auto p-4 pt-14 space-y-6">
                        <div className="flex flex-col items-center justify-center py-20">
                            <Skeleton className="h-20 w-20 rounded-3xl mb-6" />
                            <Skeleton className="h-8 w-64 mb-4" />
                            <Skeleton className="h-4 w-96 mb-8" />
                            <div className="space-y-2 w-full max-w-md">
                                <Skeleton className="h-14 rounded-2xl" />
                                <Skeleton className="h-14 rounded-2xl" />
                                <Skeleton className="h-14 rounded-2xl" />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Input Area */}
                <div className="absolute bottom-0 left-0 right-0 p-6 pt-0">
                    <div className="max-w-3xl mx-auto">
                        <Skeleton className="h-12 rounded-t-2xl" />
                        <Skeleton className="h-14 rounded-b-2xl" />
                    </div>
                </div>
            </div>
        </div>
    );
}

export function FilesSkeleton() {
    return (
        <Surface className="flex-1">
            {/* Header */}
            <div className="flex-shrink-0 p-6 lg:px-8 lg:pt-8 pb-4 border-b border-white/5">
                <div className="flex items-center justify-between">
                    <div>
                        <Skeleton className="h-8 w-40 mb-2" />
                        <Skeleton className="h-4 w-56" />
                    </div>
                    <div className="flex gap-3">
                        <Skeleton className="h-10 w-64 rounded-lg" />
                        <Skeleton className="h-10 w-24 rounded-xl" />
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex-shrink-0 px-6 lg:px-8 py-3 border-b border-white/5">
                <div className="flex gap-6">
                    {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className="h-5 w-20" />
                    ))}
                </div>
            </div>

            {/* Grid */}
            <div className="flex-1 overflow-auto p-6 lg:p-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[...Array(8)].map((_, i) => (
                        <Skeleton key={i} className="h-44 rounded-2xl" />
                    ))}
                </div>
            </div>
        </Surface>
    );
}
